import {I18n} from 'aws-amplify/utils';
import {
    Group,
    Text,
    Tooltip,
} from '@mantine/core';

import {
    shortConnectionId,
    ZoneConnectionNames,
} from '../connectionNames';

// FNP-1378 (REOPENED) — WHICH CONNECTIONS CAN REACH THIS ZONE.
//
// ⚠️ THE COLUMN CHANGED MEANING, NOT JUST SHAPE. It used to name the ONE
// connection a row came from, because a row WAS a (zone, connection) pair. A
// row is now a real zone, and several connections may reach it — so the column
// names all of them.
//
// That is what keeps the fan-out visible after grouping. Collapsing nine rows
// into three is only honest if the three still say what the nine said: a zone
// this tenant can reach through a read-only role AND an admin role is a
// different situation from one reachable through a single connection, and an
// operator about to change something needs to see which.
//
// ⚠️ NAMES, NEVER THE OPAQUE ID, and the fallback is the id's TAIL rather than
// the whole thing — `PROVIDER_CONNECTION#<tenant>#<connection>` is 80
// characters whose first two thirds are identical on every row of a tenant's
// table, so rendering it whole would widen the table to show a prefix that
// distinguishes nothing.
//
// Its own file because a module that also exports a hook or a constant breaks
// Fast Refresh, and this lints at --max-warnings 0.

/**
 * How many names are shown before the rest collapse into a "+N" affordance.
 *
 * ⚠️ A LIMIT EXISTS BECAUSE THE COLUMN MUST NOT GROW WITHOUT BOUND. A tenant
 * with eight connections into one account would otherwise push every other
 * column off the screen. Two is enough to make "more than one connection
 * reaches this" visible at a glance, which is the fact the column carries.
 */
const VISIBLE_NAMES = 2;

export const ZoneConnections = ({
    connectionIds,
    connectionNames,
}: {
    connectionIds: string[],
    connectionNames: ZoneConnectionNames,
}) => {

    // ⚠️ The name where the tenant gave one, the id's tail where they did not
    // or where the connections query has not answered yet. Never blank: an
    // unnamed row is still more use than a row that says nothing at all.
    const names = connectionIds.map(
        id => connectionNames.get(id) ?? shortConnectionId(id),
    );

    if (names.length === 0) {
        // Structurally unreachable — a group is built FROM rows and every row
        // carries a connectionId — but a blank cell is a better failure than a
        // crash inside a render.
        return <Text size={'sm'} c={'dimmed'}>—</Text>;
    }

    if (names.length === 1) {
        return <Text size={'sm'}>{names[0]}</Text>;
    }

    const shown = names.slice(0, VISIBLE_NAMES);
    const hidden = names.slice(VISIBLE_NAMES);

    return (
        <Tooltip
            label={I18n.get('This zone is reachable through these connections') + ': ' + names.join(', ')}
            withArrow={true}
            multiline={true}
            w={300}
        >
            <Group gap={'xs'} wrap={'nowrap'}>
                <Text size={'sm'}>{shown.join(', ')}</Text>
                {hidden.length > 0 && (
                    <Text size={'sm'} c={'dimmed'}>{`+${hidden.length}`}</Text>
                )}
            </Group>
        </Tooltip>
    );
};
